import type {
  AppState,
  CandidateLens,
  LensCharacterization,
  OpticalLayer,
  StatusKind,
} from './types'
import {
  candidateSimulationInputFingerprint,
  candidateToleranceInputFingerprint,
} from './candidateEvidence'

export const MODEL_LABELS = {
  analytical: 'analytical first-order estimate',
  paraxial: 'paraxial thin-lens simulation',
  geometric: 'geometric thick-lens ray trace',
  monteCarlo: 'Monte Carlo tolerance result',
  measured: 'measured or calibration-fitted result',
} as const

const EPSILON = 1e-12

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value)

export const isPositive = (value: unknown): value is number =>
  isFiniteNumber(value) && value > 0

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

export interface PanelMetrics {
  pixelPitchXmm: number | null
  pixelPitchYmm: number | null
  ppi: number | null
  missing: string[]
}

export function calculatePanelMetrics(state: Pick<AppState, 'lcd'>): PanelMetrics {
  const missing: string[] = []
  const { lcd } = state
  if (!isPositive(lcd.activeWidthMm)) missing.push('active width')
  if (!isPositive(lcd.activeHeightMm)) missing.push('active height')
  if (!isPositive(lcd.resolutionX)) missing.push('horizontal resolution')
  if (!isPositive(lcd.resolutionY)) missing.push('vertical resolution')

  const pixelPitchXmm =
    isPositive(lcd.activeWidthMm) && isPositive(lcd.resolutionX)
      ? lcd.activeWidthMm / lcd.resolutionX
      : null
  const pixelPitchYmm =
    isPositive(lcd.activeHeightMm) && isPositive(lcd.resolutionY)
      ? lcd.activeHeightMm / lcd.resolutionY
      : null
  const ppi = isPositive(pixelPitchXmm) ? 25.4 / pixelPitchXmm : null
  return { pixelPitchXmm, pixelPitchYmm, ppi, missing }
}

export interface SourceGeometry {
  sourcePitchMm: number | null
  sourceApertureMm: number | null
  repeatWidthMm: number | null
  missing: string[]
  warnings: string[]
}

export function calculateSourceGeometry(state: Pick<AppState, 'lcd' | 'view'>): SourceGeometry {
  const panel = calculatePanelMetrics(state)
  const missing: string[] = []
  const warnings: string[] = []
  let sourcePitchMm: number | null = null

  if (state.lcd.sourceMode === 'pixel-columns') {
    sourcePitchMm = panel.pixelPitchXmm
    if (!sourcePitchMm) missing.push('horizontal pixel pitch')
  } else if (state.lcd.sourceMode === 'rgb-stripe') {
    if (!isPositive(panel.pixelPitchXmm)) missing.push('horizontal pixel pitch')
    if (!isPositive(state.lcd.horizontalSubpixels)) {
      missing.push('horizontal subpixels per pixel')
    } else if (panel.pixelPitchXmm) {
      sourcePitchMm = panel.pixelPitchXmm / state.lcd.horizontalSubpixels
      warnings.push(
        'Subpixel mode couples view routing to color sampling; a simple RGB stripe is not assumed for other modes.',
      )
    }
  } else {
    if (!isPositive(state.lcd.customSourcePitchMm)) missing.push('custom source pitch')
    sourcePitchMm = isPositive(state.lcd.customSourcePitchMm)
      ? state.lcd.customSourcePitchMm
      : null
  }

  let sourceApertureMm: number | null = null
  if (state.lcd.apertureMode === 'width') {
    if (!isPositive(state.lcd.sourceApertureMm)) missing.push('source aperture width')
    sourceApertureMm = isPositive(state.lcd.sourceApertureMm)
      ? state.lcd.sourceApertureMm
      : null
  } else {
    if (!isPositive(state.lcd.sourceFillFactor) || state.lcd.sourceFillFactor > 1) {
      missing.push('source fill factor between 0 and 1')
    } else if (sourcePitchMm) {
      sourceApertureMm = sourcePitchMm * state.lcd.sourceFillFactor
    }
  }

  if (
    isFiniteNumber(state.lcd.blackMatrixWidthMm) &&
    state.lcd.blackMatrixWidthMm >= 0 &&
    sourcePitchMm
  ) {
    const fromBlackMatrix = sourcePitchMm - state.lcd.blackMatrixWidthMm
    if (fromBlackMatrix <= 0) {
      missing.push('black-matrix width smaller than source pitch')
    } else if (sourceApertureMm && Math.abs(sourceApertureMm - fromBlackMatrix) > 1e-6) {
      warnings.push(
        'Entered source aperture and black-matrix width imply different openings; the explicit aperture setting is used.',
      )
    } else if (!sourceApertureMm) {
      sourceApertureMm = fromBlackMatrix
    }
  }

  const views = Number.isInteger(state.view.views) && state.view.views >= 2 ? state.view.views : null
  if (!views) missing.push('number of views (at least 2)')
  const repeatWidthMm = sourcePitchMm && views ? sourcePitchMm * views : null
  return { sourcePitchMm, sourceApertureMm, repeatWidthMm, missing, warnings }
}

export interface ReducedGap {
  reducedGapMm: number | null
  physicalThicknessMm: number
  usedLayers: number
  missing: string[]
  invalid: string[]
}

export function calculateReducedGap(layers: OpticalLayer[]): ReducedGap {
  let reducedGapMm = 0
  let physicalThicknessMm = 0
  let usedLayers = 0
  const missing: string[] = []
  const invalid: string[] = []

  layers.forEach((layer, index) => {
    if (!layer.enabled) return
    const label = layer.name.trim() || `Layer ${index + 1}`
    if (!isPositive(layer.thicknessMm)) missing.push(`${label}: thickness`)
    if (!isPositive(layer.refractiveIndex)) missing.push(`${label}: refractive index`)
    if (!isPositive(layer.thicknessMm) || !isPositive(layer.refractiveIndex)) return
    if (layer.refractiveIndex < 1) invalid.push(`${label}: refractive index is below 1`)
    reducedGapMm += layer.thicknessMm / layer.refractiveIndex
    physicalThicknessMm += layer.thicknessMm
    usedLayers += 1
  })

  if (layers.filter((layer) => layer.enabled).length === 0) {
    missing.push('at least one enabled optical layer')
  }

  return {
    reducedGapMm: missing.length || invalid.length ? null : reducedGapMm,
    physicalThicknessMm,
    usedLayers,
    missing,
    invalid,
  }
}

export interface AnalyticalDesign {
  model: typeof MODEL_LABELS.analytical
  panel: PanelMetrics
  source: SourceGeometry
  gap: ReducedGap
  targetDistanceMm: number | null
  requiredFocalLengthMm: number | null
  requiredPitchMm: number | null
  requiredLpi: number | null
  infinitePitchMm: number | null
  infiniteLpi: number | null
  infiniteLpiFromPpi: number | null
  adjacentViewSpacingMm: number | null
  targetDistanceForSpacingMm: number | null
  missing: string[]
  warnings: string[]
}

export function calculateAnalyticalDesign(state: AppState): AnalyticalDesign {
  const panel = calculatePanelMetrics(state)
  const source = calculateSourceGeometry(state)
  const gap = calculateReducedGap(state.layers)
  const missing = [...source.missing, ...gap.missing, ...gap.invalid]
  const warnings = [...source.warnings]
  const z = isPositive(state.view.targetDistanceMm) ? state.view.targetDistanceMm : null
  if (!z) missing.push('target viewing distance')
  const d = gap.reducedGapMm
  const q = source.sourcePitchMm
  const G = source.repeatWidthMm

  const requiredFocalLengthMm = z && d ? (z * d) / (z + d) : null
  const requiredPitchMm = z && d && G ? (G * z) / (z + d) : null
  const requiredLpi = requiredPitchMm ? 25.4 / requiredPitchMm : null
  const infinitePitchMm = G
  const infiniteLpi = G ? 25.4 / G : null
  const infiniteLpiFromPpi =
    state.lcd.sourceMode === 'pixel-columns' && panel.ppi && state.view.views >= 2
      ? panel.ppi / state.view.views
      : null
  const adjacentViewSpacingMm = z && q && d ? (z * q) / d : null
  const targetDistanceForSpacingMm =
    isPositive(state.view.targetSpacingMm) && d && q
      ? (state.view.targetSpacingMm * d) / q
      : null

  if (z && d && d / z > 0.05) {
    warnings.push('The source gap is large relative to viewing distance; verify first-order assumptions.')
  }

  return {
    model: MODEL_LABELS.analytical,
    panel,
    source,
    gap,
    targetDistanceMm: z,
    requiredFocalLengthMm,
    requiredPitchMm,
    requiredLpi,
    infinitePitchMm,
    infiniteLpi,
    infiniteLpiFromPpi,
    adjacentViewSpacingMm,
    targetDistanceForSpacingMm,
    missing: [...new Set(missing)],
    warnings,
  }
}

export type ImageDistanceResult =
  | { state: 'real'; distanceMm: number; message: string }
  | { state: 'collimated'; distanceMm: null; message: string }
  | { state: 'virtual'; distanceMm: null; message: string }
  | { state: 'insufficient'; distanceMm: null; message: string }

export function predictedImageDistance(
  focalLengthMm: number | null,
  reducedGapMm: number | null,
): ImageDistanceResult {
  if (!isPositive(focalLengthMm) || !isPositive(reducedGapMm)) {
    return {
      state: 'insufficient',
      distanceMm: null,
      message: 'Focal length and reduced optical gap are required.',
    }
  }
  const delta = reducedGapMm - focalLengthMm
  const nearThreshold = Math.max(1e-6, Math.max(reducedGapMm, focalLengthMm) * 1e-4)
  if (Math.abs(delta) <= nearThreshold) {
    return {
      state: 'collimated',
      distanceMm: null,
      message: 'Source is approximately at the focal plane; output is approximately collimated.',
    }
  }
  if (delta < 0) {
    return {
      state: 'virtual',
      distanceMm: null,
      message: 'Reduced gap is shorter than focal length; no finite real image forms in front.',
    }
  }
  return {
    state: 'real',
    distanceMm: (focalLengthMm * reducedGapMm) / delta,
    message: 'Finite real image in front of the modeled thin-lens plane.',
  }
}

export interface PitchMismatch {
  pitchErrorMm: number
  pitchErrorUm: number
  pitchErrorPercent: number
  lpiError: number
  mismatchSourceCells: number
  accumulatedDriftMm: number
  driftSourceCells: number
  driftLensPeriods: number
}

export function calculatePitchMismatch(
  actualPitchMm: number | null,
  requiredPitchMm: number | null,
  activeWidthMm: number | null,
  sourcePitchMm: number | null,
): PitchMismatch | null {
  if (
    !isPositive(actualPitchMm) ||
    !isPositive(requiredPitchMm) ||
    !isPositive(activeWidthMm) ||
    !isPositive(sourcePitchMm)
  ) {
    return null
  }
  const pitchErrorMm = actualPitchMm - requiredPitchMm
  const accumulatedDriftMm = (activeWidthMm / requiredPitchMm) * pitchErrorMm
  return {
    pitchErrorMm,
    pitchErrorUm: pitchErrorMm * 1000,
    pitchErrorPercent: (pitchErrorMm / requiredPitchMm) * 100,
    lpiError: 25.4 / actualPitchMm - 25.4 / requiredPitchMm,
    mismatchSourceCells: pitchErrorMm / sourcePitchMm,
    accumulatedDriftMm,
    driftSourceCells: accumulatedDriftMm / sourcePitchMm,
    driftLensPeriods: accumulatedDriftMm / actualPitchMm,
  }
}

function finiteDifference(
  fn: (value: number) => number | null,
  value: number,
  relativeStep = 1e-5,
): number | null {
  const h = Math.max(Math.abs(value) * relativeStep, 1e-7)
  const high = fn(value + h)
  const low = fn(value - h)
  if (!isFiniteNumber(high) || !isFiniteNumber(low)) return null
  return (high - low) / (2 * h)
}

export interface Sensitivities {
  viewingDistancePerFocalLength: number | null
  viewSpacingPerReducedGap: number | null
  requiredPitchPerViewingDistance: number | null
  accumulatedDriftPerPitchError: number | null
}

export function calculateSensitivities(state: AppState): Sensitivities {
  const design = calculateAnalyticalDesign(state)
  const d = design.gap.reducedGapMm
  const z = design.targetDistanceMm
  const q = design.source.sourcePitchMm
  const G = design.source.repeatWidthMm
  const f = state.lens.thin.useDesignTarget
    ? design.requiredFocalLengthMm
    : state.lens.thin.focalLengthMm

  return {
    viewingDistancePerFocalLength:
      isPositive(d) && isPositive(f)
        ? finiteDifference((candidateF) => {
            const result = predictedImageDistance(candidateF, d)
            return result.state === 'real' ? result.distanceMm : null
          }, f)
        : null,
    viewSpacingPerReducedGap:
      isPositive(z) && isPositive(q) && isPositive(d)
        ? finiteDifference((candidateD) => (z * q) / candidateD, d)
        : null,
    requiredPitchPerViewingDistance:
      isPositive(z) && isPositive(d) && isPositive(G)
        ? finiteDifference((candidateZ) => (G * candidateZ) / (candidateZ + d), z)
        : null,
    accumulatedDriftPerPitchError:
      isPositive(state.lcd.activeWidthMm) && isPositive(design.requiredPitchMm)
        ? finiteDifference(
            (pitchError) =>
              (state.lcd.activeWidthMm! / design.requiredPitchMm!) * pitchError,
            0,
          )
        : null,
  }
}

export interface ThickGeometryValidation {
  valid: boolean
  sagMm: number | null
  edgeThicknessMm: number | null
  errors: string[]
}

export function validateThickGeometry(
  pitchMm: number | null,
  radiusMm: number | null,
  centerThicknessMm: number | null,
): ThickGeometryValidation {
  const errors: string[] = []
  if (!isPositive(pitchMm)) errors.push('lens pitch')
  if (!isPositive(radiusMm)) errors.push('radius of curvature')
  if (!isPositive(centerThicknessMm)) errors.push('center thickness')
  if (errors.length) return { valid: false, sagMm: null, edgeThicknessMm: null, errors }
  if (radiusMm! < pitchMm! / 2) {
    return {
      valid: false,
      sagMm: null,
      edgeThicknessMm: null,
      errors: ['Radius must be at least half the lens pitch.'],
    }
  }
  const sagMm = radiusMm! - Math.sqrt(radiusMm! ** 2 - (pitchMm! / 2) ** 2)
  const edgeThicknessMm = centerThicknessMm! - sagMm
  if (edgeThicknessMm < 0) {
    errors.push('Center thickness is smaller than the circular sag; edge thickness is negative.')
  }
  return { valid: errors.length === 0, sagMm, edgeThicknessMm, errors }
}

interface Vec2 {
  x: number
  y: number
}

function normalize(vector: Vec2): Vec2 {
  const length = Math.hypot(vector.x, vector.y)
  return length > EPSILON ? { x: vector.x / length, y: vector.y / length } : { x: 0, y: 0 }
}

export function refractVector(
  incident: Vec2,
  outwardNormal: Vec2,
  nIncident: number,
  nTransmitted: number,
): Vec2 | null {
  const i = normalize(incident)
  let n = normalize(outwardNormal)
  let cosine = i.x * n.x + i.y * n.y
  if (cosine < 0) {
    n = { x: -n.x, y: -n.y }
    cosine = -cosine
  }
  const eta = nIncident / nTransmitted
  const discriminant = 1 - eta * eta * (1 - cosine * cosine)
  if (discriminant < 0) return null
  return normalize({
    x: eta * i.x + (Math.sqrt(discriminant) - eta * cosine) * n.x,
    y: eta * i.y + (Math.sqrt(discriminant) - eta * cosine) * n.y,
  })
}

export function estimateThickParaxial(
  pitchMm: number | null,
  radiusMm: number | null,
  refractiveIndex: number | null,
  centerThicknessMm: number | null,
  externalIndex: number | null,
): LensCharacterization {
  const empty: LensCharacterization = {
    paraxialBackFocalDistanceMm: null,
    effectiveFocalLengthMm: null,
    principalPlaneFromRearMm: null,
    outputAngularSpreadDeg: null,
    relativeThroughput: 0,
    tirFraction: 0,
    chromaticShiftMm: {},
  }
  const geometry = validateThickGeometry(pitchMm, radiusMm, centerThicknessMm)
  if (
    !geometry.valid ||
    !isPositive(refractiveIndex) ||
    !isPositive(externalIndex) ||
    refractiveIndex <= externalIndex
  ) {
    return empty
  }

  const focusPositions: number[] = []
  const focalFromSlope: number[] = []
  const heights = [0.005, 0.01, 0.015, 0.02, 0.025].map((fraction) => pitchMm! * fraction)
  for (const height of heights) {
    const ySurface =
      centerThicknessMm! - radiusMm! + Math.sqrt(radiusMm! ** 2 - height ** 2)
    const normal = normalize({
      x: height,
      y: ySurface - (centerThicknessMm! - radiusMm!),
    })
    const out = refractVector({ x: 0, y: 1 }, normal, refractiveIndex, externalIndex)
    if (!out || Math.abs(out.x) < EPSILON || out.y <= 0) continue
    const travel = -height / out.x
    if (travel <= 0) continue
    const focusY = ySurface + travel * out.y
    const angleSlope = out.x / out.y
    focusPositions.push(focusY)
    focalFromSlope.push(-height / angleSlope)
  }

  if (!focusPositions.length) return empty
  const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length
  const focusFromRear = average(focusPositions)
  const effectiveFocalLengthMm = average(focalFromSlope)
  return {
    ...empty,
    paraxialBackFocalDistanceMm: focusFromRear - centerThicknessMm!,
    effectiveFocalLengthMm,
    principalPlaneFromRearMm: focusFromRear - effectiveFocalLengthMm,
    relativeThroughput: 1,
  }
}

export function predictedThickImageDistance(
  characterization: LensCharacterization,
  reducedGapMm: number | null,
  centerThicknessMm: number | null,
  externalIndex: number | null,
): ImageDistanceResult {
  const focalLengthMm = characterization.effectiveFocalLengthMm
  const principalPlaneFromRearMm = characterization.principalPlaneFromRearMm
  if (
    !isPositive(focalLengthMm) ||
    !isPositive(reducedGapMm) ||
    !isFiniteNumber(principalPlaneFromRearMm) ||
    !isPositive(centerThicknessMm) ||
    !isPositive(externalIndex)
  ) {
    return {
      state: 'insufficient',
      distanceMm: null,
      message: 'Numerical EFL, principal plane, reduced gap, thickness, and external index are required.',
    }
  }

  const objectDistanceFromPrincipalMm =
    reducedGapMm * externalIndex + principalPlaneFromRearMm
  if (!isPositive(objectDistanceFromPrincipalMm)) {
    return {
      state: 'insufficient',
      distanceMm: null,
      message: 'The effective object distance from the numerical principal plane is not positive.',
    }
  }
  const delta = objectDistanceFromPrincipalMm - focalLengthMm
  const nearThreshold = Math.max(
    1e-6,
    Math.max(objectDistanceFromPrincipalMm, focalLengthMm) * 1e-4,
  )
  if (Math.abs(delta) <= nearThreshold) {
    return {
      state: 'collimated',
      distanceMm: null,
      message: 'Source is approximately at the numerical thick-lens focal plane.',
    }
  }
  if (delta < 0) {
    return {
      state: 'virtual',
      distanceMm: null,
      message: 'No finite real image forms in front under the numerical thick-lens convention.',
    }
  }

  const imageDistanceFromPrincipalMm =
    (focalLengthMm * objectDistanceFromPrincipalMm) / delta
  const distanceFromFrontVertexMm =
    principalPlaneFromRearMm + imageDistanceFromPrincipalMm - centerThicknessMm
  if (!isPositive(distanceFromFrontVertexMm)) {
    return {
      state: 'virtual',
      distanceMm: null,
      message: 'The numerical conjugate does not lie in front of the lens vertex.',
    }
  }
  return {
    state: 'real',
    distanceMm: distanceFromFrontVertexMm,
    message: 'Finite conjugate from numerical near-axis thick-lens cardinal behavior.',
  }
}

export function targetCenters(state: Pick<AppState, 'view'>): {
  centers: number[]
  missing: string[]
} {
  const { view } = state
  const missing: string[] = []
  if (!Number.isInteger(view.views) || view.views < 2) {
    return { centers: [], missing: ['number of views'] }
  }
  if (view.centerMode === 'custom') {
    if (
      view.customCentersMm.length !== view.views ||
      view.customCentersMm.some((value) => !isFiniteNumber(value))
    ) {
      return { centers: [], missing: [`${view.views} finite custom target centers`] }
    }
    return { centers: [...view.customCentersMm], missing }
  }
  if (view.centerMode === 'two-view-eyes') {
    if (view.views !== 2) missing.push('exactly two views for eye-placement mode')
    if (!isPositive(view.eyeSeparationMm)) missing.push('eye separation')
    if (missing.length) return { centers: [], missing }
    return { centers: [-view.eyeSeparationMm! / 2, view.eyeSeparationMm! / 2], missing }
  }
  if (!isPositive(view.targetSpacingMm)) return { centers: [], missing: ['target view spacing'] }
  return {
    centers: Array.from(
      { length: view.views },
      (_, index) => (index - (view.views - 1) / 2) * view.targetSpacingMm!,
    ),
    missing,
  }
}

export interface CandidateEvaluation {
  status: StatusKind
  label: string
  scope: string
  analyticalStatus: StatusKind
  analyticalLabel: string
  reasons: string[]
  inputMissing: string[]
  evidenceMissing: string[]
  missing: string[]
  pitchMm: number | null
  lpi: number | null
  mismatch: PitchMismatch | null
  focalBehaviorMm: number | null
  focalBehaviorLabel: string
  requiredFocalBehaviorMm: number | null
  focalBehaviorErrorMm: number | null
  predictedViewingDistance: ImageDistanceResult
  predictedSpacingMm: number | null
  simulationEvidenceCurrent: boolean
  simulationEvidenceStale: boolean
  monteCarloEvidenceCurrent: boolean
  monteCarloEvidenceStale: boolean
  thickCharacterization?: LensCharacterization
}

const statusLabel: Record<StatusKind, string> = {
  'design-target': 'Design target, not screened',
  insufficient: 'Nominal screen incomplete',
  meets: 'Passes enabled nominal v1 checks',
  marginal: 'Borderline on enabled nominal v1 checks',
  fails: 'Fails one or more enabled nominal v1 checks',
}

const analyticalStatusLabel: Record<StatusKind, string> = {
  'design-target': 'Design target, not analyzed',
  insufficient: 'Analytical inputs incomplete',
  meets: 'Passes enabled analytical checks',
  marginal: 'Borderline on enabled analytical checks',
  fails: 'Fails one or more enabled analytical checks',
}

function classifyThresholds(results: Array<'pass' | 'marginal' | 'fail'>): StatusKind {
  if (results.includes('fail')) return 'fails'
  if (results.includes('marginal')) return 'marginal'
  return 'meets'
}

function classifyMax(value: number, limit: number, marginalMultiplier: number) {
  const magnitude = Math.abs(value)
  if (magnitude <= limit) return 'pass' as const
  if (magnitude <= limit * marginalMultiplier) return 'marginal' as const
  return 'fail' as const
}

function classifyMin(value: number, limit: number, marginalMultiplier: number) {
  if (value >= limit) return 'pass' as const
  if (value >= limit / marginalMultiplier) return 'marginal' as const
  return 'fail' as const
}

export function evaluateCandidate(candidate: CandidateLens, state: AppState): CandidateEvaluation {
  const design = calculateAnalyticalDesign(state)
  const missing: string[] = []
  const reasons: string[] = []
  const marginal = state.view.criteria.marginalMultiplier
  const simulationEvidenceCurrent = Boolean(
    candidate.simulated &&
      candidate.simulated.candidateId === candidate.id &&
      candidate.simulated.inputFingerprint ===
        candidateSimulationInputFingerprint(candidate, state),
  )
  const simulationEvidenceStale = Boolean(candidate.simulated && !simulationEvidenceCurrent)
  const monteCarloEvidenceCurrent = Boolean(
    candidate.monteCarlo &&
      candidate.monteCarlo.candidateId === candidate.id &&
      candidate.monteCarlo.inputFingerprint ===
        candidateToleranceInputFingerprint(candidate, state),
  )
  const monteCarloEvidenceStale = Boolean(candidate.monteCarlo && !monteCarloEvidenceCurrent)
  const simulationEvidence = simulationEvidenceCurrent ? candidate.simulated : undefined
  if (simulationEvidenceStale) {
    reasons.push('Attached simulation evidence is stale for the current candidate or global inputs.')
  }
  if (monteCarloEvidenceStale) {
    reasons.push('Attached Monte Carlo evidence is stale for the current candidate or global inputs.')
  }
  const pitchMm = isPositive(candidate.pitchMm)
    ? candidate.pitchMm
    : isPositive(candidate.lpi)
      ? 25.4 / candidate.lpi
      : null
  const lpi = pitchMm ? 25.4 / pitchMm : null
  if (!pitchMm) missing.push('candidate pitch or LPI')
  if (!design.requiredPitchMm) missing.push(...design.missing)

  const mismatch = calculatePitchMismatch(
    pitchMm,
    design.requiredPitchMm,
    state.lcd.activeWidthMm,
    design.source.sourcePitchMm,
  )
  const analyticalChecks: Array<'pass' | 'marginal' | 'fail'> = []
  if (state.view.criteria.enabled.phaseDrift) {
    if (!mismatch) {
      missing.push('pitch drift inputs')
    } else {
      const outcome = classifyMax(
        mismatch.driftSourceCells,
        state.view.criteria.maxAccumulatedDriftSourceCells,
        marginal,
      )
      analyticalChecks.push(outcome)
      reasons.push(
        `Accumulated drift ${Math.abs(mismatch.driftSourceCells).toFixed(3)} source cells (${outcome}; limit ${state.view.criteria.maxAccumulatedDriftSourceCells}, marginal band ×${marginal}).`,
      )
    }
  }

  let focalBehaviorMm: number | null = null
  let focalBehaviorLabel = 'Not characterized'
  let requiredFocalBehaviorMm: number | null = design.requiredFocalLengthMm
  let predictedViewingDistance: ImageDistanceResult = {
    state: 'insufficient',
    distanceMm: null,
    message: 'Optical power data are required.',
  }
  let thickCharacterization: LensCharacterization | undefined

  if (candidate.mode === 'thin') {
    if (!isPositive(candidate.focalLengthMm)) missing.push('candidate focal length')
    if (candidate.focalReference === 'vendor-unspecified') {
      missing.push('unambiguous focal-length reference convention')
      reasons.push('Vendor focal length is not used because its reference plane is unspecified.')
    } else if (isPositive(candidate.focalLengthMm)) {
      focalBehaviorMm = candidate.focalLengthMm
      focalBehaviorLabel =
        candidate.focalReference === 'effective-at-plane'
          ? 'Effective focal length at ideal thin-lens plane'
          : 'Back focal distance referenced to ideal thin-lens plane'
      predictedViewingDistance = predictedImageDistance(
        candidate.focalLengthMm,
        design.gap.reducedGapMm,
      )
    }
  } else {
    const geometry = validateThickGeometry(
      pitchMm,
      candidate.radiusMm,
      candidate.centerThicknessMm,
    )
    if (!geometry.valid) missing.push(...geometry.errors)
    if (!isPositive(candidate.refractiveIndex)) missing.push('lens refractive index')
    const externalIndex = state.lens.thick.externalIndex
    thickCharacterization = estimateThickParaxial(
      pitchMm,
      candidate.radiusMm,
      candidate.refractiveIndex,
      candidate.centerThicknessMm,
      externalIndex,
    )
    focalBehaviorMm = thickCharacterization.effectiveFocalLengthMm
    focalBehaviorLabel = 'Numerical near-axis effective focal behavior'
    predictedViewingDistance = predictedThickImageDistance(
      thickCharacterization,
      design.gap.reducedGapMm,
      candidate.centerThicknessMm,
      externalIndex,
    )
    if (
      isPositive(design.gap.reducedGapMm) &&
      isPositive(state.view.targetDistanceMm) &&
      isFiniteNumber(thickCharacterization.principalPlaneFromRearMm) &&
      isPositive(candidate.centerThicknessMm) &&
      isPositive(externalIndex)
    ) {
      const objectDistanceMm =
        design.gap.reducedGapMm * externalIndex +
        thickCharacterization.principalPlaneFromRearMm
      const imageDistanceMm =
        state.view.targetDistanceMm +
        candidate.centerThicknessMm -
        thickCharacterization.principalPlaneFromRearMm
      requiredFocalBehaviorMm =
        isPositive(objectDistanceMm) && isPositive(imageDistanceMm)
          ? (objectDistanceMm * imageDistanceMm) / (objectDistanceMm + imageDistanceMm)
          : null
    } else {
      requiredFocalBehaviorMm = null
    }
  }

  const focalBehaviorErrorMm =
    isPositive(focalBehaviorMm) && isPositive(requiredFocalBehaviorMm)
      ? focalBehaviorMm - requiredFocalBehaviorMm
      : null
  if (isPositive(focalBehaviorMm) && isPositive(requiredFocalBehaviorMm)) {
    reasons.push(
      `Required focal behavior ${requiredFocalBehaviorMm.toFixed(6)} mm versus actual ${focalBehaviorMm.toFixed(6)} mm (${focalBehaviorErrorMm! >= 0 ? '+' : ''}${focalBehaviorErrorMm!.toFixed(6)} mm; ${candidate.mode === 'thick' ? 'numerical near-axis thick-lens cardinal estimate' : 'analytical first-order estimate'}).`,
    )
  }

  if (state.view.criteria.enabled.viewingRange) {
    if (
      predictedViewingDistance.state !== 'real' ||
      !isPositive(state.view.distanceMinMm) ||
      !isPositive(state.view.distanceMaxMm)
    ) {
      missing.push('finite predicted viewing distance and viewing-distance range')
    } else {
      const z = predictedViewingDistance.distanceMm
      const min = state.view.distanceMinMm
      const max = state.view.distanceMaxMm
      if (z >= min && z <= max) {
        analyticalChecks.push('pass')
        reasons.push(`Predicted viewing distance ${z.toFixed(1)} mm is inside ${min}–${max} mm.`)
      } else {
        const distanceToRange = z < min ? min - z : z - max
        const rangeWidth = Math.max(max - min, 1)
        const outcome = distanceToRange <= rangeWidth * (marginal - 1) ? 'marginal' : 'fail'
        analyticalChecks.push(outcome)
        reasons.push(
          `Predicted viewing distance ${z.toFixed(1)} mm is outside ${min}–${max} mm (${outcome}).`,
        )
      }
    }
  }

  const inputMissing = [...new Set(missing)]
  const analyticalStatus: StatusKind = inputMissing.length
    ? 'insufficient'
    : classifyThresholds(analyticalChecks)
  const overallChecks = [...analyticalChecks]
  const evidenceMissing: string[] = []

  if (state.view.criteria.enabled.crosstalk) {
    if (!simulationEvidence) evidenceMissing.push('current simulated crosstalk')
    else {
      const outcome = classifyMax(
        simulationEvidence.crosstalkMax,
        state.view.criteria.maxCrosstalk,
        marginal,
      )
      overallChecks.push(outcome)
      reasons.push(
        `Worst simulated crosstalk ${(simulationEvidence.crosstalkMax * 100).toFixed(1)}% (${outcome}; limit ${(state.view.criteria.maxCrosstalk * 100).toFixed(1)}%).`,
      )
    }
  }
  if (state.view.criteria.enabled.zoneCenter) {
    if (!simulationEvidence) evidenceMissing.push('current simulated zone-center error')
    else {
      const outcome = classifyMax(
        simulationEvidence.zoneCenterErrorMaxMm,
        state.view.criteria.maxZoneCenterErrorMm,
        marginal,
      )
      overallChecks.push(outcome)
      reasons.push(
        `Worst zone-center error ${simulationEvidence.zoneCenterErrorMaxMm.toFixed(2)} mm (${outcome}; limit ${state.view.criteria.maxZoneCenterErrorMm} mm).`,
      )
    }
  }
  if (state.view.criteria.enabled.throughput) {
    if (!simulationEvidence) evidenceMissing.push('current simulated desired-view throughput')
    else {
      const outcome = classifyMin(
        simulationEvidence.throughputMin,
        state.view.criteria.minRelativeThroughput,
        marginal,
      )
      overallChecks.push(outcome)
      reasons.push(
        `Minimum desired-view throughput ${(simulationEvidence.throughputMin * 100).toFixed(1)}% (${outcome}; minimum ${(state.view.criteria.minRelativeThroughput * 100).toFixed(1)}%).`,
      )
    }
  }

  const dedupedEvidenceMissing = [...new Set(evidenceMissing)]
  const dedupedMissing = [...new Set([...inputMissing, ...dedupedEvidenceMissing])]
  const status: StatusKind = dedupedMissing.length ? 'insufficient' : classifyThresholds(overallChecks)
  const predictedSpacingMm =
    isPositive(state.view.targetDistanceMm) &&
    isPositive(design.source.sourcePitchMm) &&
    isPositive(design.gap.reducedGapMm)
      ? (state.view.targetDistanceMm * design.source.sourcePitchMm) /
        design.gap.reducedGapMm
      : null
  if (isPositive(predictedSpacingMm)) {
    reasons.push(
      `Predicted adjacent zone spacing ${predictedSpacingMm.toFixed(3)} mm at the target plane (analytical first-order estimate).`,
    )
  }

  return {
    status,
    label: statusLabel[status],
    scope: 'Enabled analytical and trace-based v1 checks',
    analyticalStatus,
    analyticalLabel: analyticalStatusLabel[analyticalStatus],
    reasons,
    inputMissing,
    evidenceMissing: dedupedEvidenceMissing,
    missing: dedupedMissing,
    pitchMm,
    lpi,
    mismatch,
    focalBehaviorMm,
    focalBehaviorLabel,
    requiredFocalBehaviorMm,
    focalBehaviorErrorMm,
    predictedViewingDistance,
    predictedSpacingMm,
    simulationEvidenceCurrent,
    simulationEvidenceStale,
    monteCarloEvidenceCurrent,
    monteCarloEvidenceStale,
    thickCharacterization,
  }
}

export function syncPitchFromLpi(lpi: number | null): number | null {
  return isPositive(lpi) ? 25.4 / lpi : null
}

export function syncLpiFromPitch(pitchMm: number | null): number | null {
  return isPositive(pitchMm) ? 25.4 / pitchMm : null
}
